import React, {
  type ReactElement,
  type UIEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  type RowDataItem,
  TableSortable,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../hooks';

import './DataTable.css';

interface DataTableProps {
  data: RowDataItem[];
  columns: { key: string; label: string; [key: string]: unknown }[];
  sortConfigs?: Record<string, unknown>[];
  searchable?: boolean;
  className?: string;
  tableHeight?: number;
  isVirtualizationEnabled?: boolean;
  onRowClick?: () => void;
  hasMoreData?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

function DataTable({
  data,
  columns: initialColumns,
  sortConfigs,
  searchable = false,
  tableHeight = 400,
  isVirtualizationEnabled = true,
  onRowClick,
  hasMoreData = false,
  isLoadingMore = false,
  onLoadMore,
  className = '',
}: DataTableProps): ReactElement {
  const [columns, setColumns] = useState(initialColumns);
  const [searchValue] = useState('');
  const l = useInvertedLocalize();

  const filteredData = useMemo(() => {
    if (!searchable || !searchValue) {
      return data;
    }

    return data.filter((row) =>
      Object.values(row as Record<string, unknown>).some((value) => {
        if (value === null || value === undefined) {
          return false;
        }

        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [data, searchable, searchValue]);

  // Handle scroll event to load more data
  const handleScroll = useCallback(
    (e: UIEvent<HTMLElement>): void => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

      // Check if scrolled to bottom (with 10px threshold)
      if (
        scrollHeight - scrollTop <= clientHeight + 10 &&
        hasMoreData &&
        !isLoadingMore &&
        onLoadMore
      ) {
        onLoadMore();
      }
    },
    [hasMoreData, isLoadingMore, onLoadMore]
  );

  return (
    <Box className={`FF-DataTable ${className}`}>
      {/* {searchable && (
        <Box className='FF-DataTable-search'>
          <InputField
            name='datatable-search'
            kind='search'
            placeholder={searchPlaceholder}
            value={searchValue}
            setValue={setSearchValue}
            prefixIcon={<Icon kind='Search' color={TOKENS.CF_NEU_STL} />}
          />
        </Box>
      )} */}
      <TableSortable
        data={filteredData}
        columns={columns}
        setColumns={setColumns}
        sortConfigs={sortConfigs}
        isVirtualizationEnabled={isVirtualizationEnabled}
        isWindowScroll={false}
        tableHeight={tableHeight}
        onRowClick={onRowClick}
        className='FF-DataTable-table'
        onScroll={handleScroll}
      />

      {/* Loading Indicator */}
      {isLoadingMore && (
        <Box className='FF-DataTable-loading'>
          <Typography kind='body2' color='neutral-medium'>
            {l('Loading more data...')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default DataTable;
